import React, { useState } from "react";
import { navigate } from "gatsby"
import axios from 'axios';
import { 
  Formik,
  Form, 
  Field,
  ErrorMessage 
} from 'formik';
import * as yup from 'yup';
import { Mixpanel } from '../../utils/mixpanel';
import Loader from '../loader/loader';
import './signupFormStyle.css';

const userApiClient = axios.create({
  baseURL: process.env.GATSBY_BASE_API_URL,
  // baseURL: 'https://api.sunset.health/api/v1',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 4000,
})


let regex = /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g
let email_regex_validator = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,63})+$/;
let password_regex_validator = /^\d*[a-zA-Z][a-zA-Z\d]*$/;
let validation = yup.object().shape({
    first_name: yup
            .string()
            .required('First name is required'),
    last_name: yup
            .string()
            .required('Last name is required'),
    employer_name: yup
            .string()
            .required('Company name is required'),
    email: yup
            .string()
            .email('Not a valid email')
            .required('Email is required'),
    password: yup
            .string()
            .min(8, 'Password must have at least 8 characters.')
            .matches(password_regex_validator, 'Can only contain letters and numbers')
            .required('Password is required'),
    confirm_password: yup
            .string()
            .required('Password is required')
            .test('passwords-match', 'Passwords must match', function(value) {
              return this.parent.password === value;
            }),
});

const SignUpForm = ({onSubmitHandler, fontColor, stripe_purchase_flow, slug, employer, dispatch, orgID, children}) => {

  const [emailError, setEmailError] = useState(false);
  const [orgError, setOrgError] = useState(false);
  const [unkError, setUnkError] = useState(false);
  const [loading, setLoading] = useState(false);

  async function loadData () {
    setLoading(true);
  }

  return (
    <>
      <Formik
        initialValues={{
          first_name: "",
          last_name: "",
          employer_name: "",
          email: "",
          password: "",
          confirm_password: "",
          employer_code: "",
          insurance_carrier: ''
        }}
        validationSchema={validation}
        onSubmit={(values) => {
          loadData();
          setOrgError(false);
          setEmailError(false);
          setUnkError(false);
          userApiClient
            .post('auth', { 
                'first_name': values.first_name || '', 
                'last_name': values.last_name || '', 
                'name': values.first_name + ' ' + values.last_name || '', 
                'age': values.age || '', 
                'employer_name': values.employer_name || '',
                'email': values.email || '', 
                'password': values.password || '', 
                'password_confirmation': values.password || '',
                'employer_code': values.employer_code || '',
                'insurance_carrier': values.insurance_carrier || ''
            })
            .then((response) => {
              const {data: {data}} = response;
              Mixpanel.people.set({
                $email: data['email'],
                $first_name: data['first_name'],
                $last_name: data['last_name'],
                $employer_name: data['employer_name'],
                $employer_code: data['employer_code'],
              });
              Mixpanel.track('Event: User signed up from Karen Thompson Portal', 
              {
                email: data.email, 
                employerName: data['employer_name'],
                employer_code: data['employer_code'],
              })
              const USER_DATA = {
                meta: {...data.meta},
                email: values.email,
                org_id: orgID
              }
              if (stripe_purchase_flow) {
                localStorage.setItem('user-em', JSON.stringify(USER_DATA));
                onSubmitHandler(values.email);
              } else {
                localStorage.setItem('user-em', JSON.stringify(USER_DATA));
                navigate('/premium-submission')
              }
            })
            .catch(error => {
              if (!error.response) {
                setUnkError(true);
                setLoading(false);
              } else {
                if (error.response.status === 404) {
                  navigate('/404')
                } else if (error.response.status === 422) {
                  if (error.response.data.errors.Organization 
                    && error.response.data.errors.Organization === "Code Not Valid") {
                      setOrgError(true);
                      setLoading(false);
                    }
                  else if (error.response.data.errors.email) {
                    setEmailError(true);
                    setLoading(false);
                  }
                } else {
                  setUnkError(true);
                  setLoading(false);
                }
              }
            })
        }}
      >
        { formik => {
            return (
              <Form className="form">
                <div className="form-title">Create your Sunset Health account</div>
                  <div className='input-container name-container'>
                    <div className='name-wrapper'>
                      <div className='label-wrapper'>
                        <label className='input-label' htmlFor="first_name" style={{ color: fontColor }}>First Name</label>
                      </div>
                      <Field
                        className='form-input'
                        type='text'
                        placeholder={'Enter your first name'}
                        name='first_name'
                        style={{ borderColor: fontColor }}
                      />
                      <ErrorMessage className='error' component="div" name='first_name' />
                    </div>
                    <div className='name-wrapper'>
                      <div className='label-wrapper'>
                        <label className='input-label' htmlFor="last_name" style={{ color: fontColor }}>Last Name</label>
                      </div>
                      <Field
                        className='form-input'
                        type='text'
                        placeholder={'Enter your last name'}
                        name='last_name'
                        style={{ borderColor: fontColor }}
                      />
                      <ErrorMessage className='error' component="div" name='last_name' />
                    </div>
                  </div>
                  <div className='input-container'>
                    <div className='label-wrapper'>
                      <label className='input-label' htmlFor="email" style={{ color: fontColor }}>Email Address</label>
                    </div>
                    <Field
                      className='form-input'
                      type='text'
                      placeholder={'Enter your email address'}
                      name='email'
                      onKeyDown={() => setEmailError(false)}
                      style={{ borderColor: fontColor }}
                    />
                    <ErrorMessage className='error' component="div" name='email' />
                    {
                      emailError && (<div className='error'>Email has already been taken</div>)
                    }
                  </div>
                  <div className='input-container'>
                    <div className='label-wrapper'>
                      <label className='input-label' htmlFor="password" style={{ color: fontColor }}>Password</label>
                    </div>
                    <Field
                      className='form-input'
                      type='password'
                      placeholder={'Enter your password (8+ characters)'}
                      name='password'
                      style={{ borderColor: fontColor }}
                    />
                    <ErrorMessage className='error' component="div" name='password' />
                  </div>
                  <div className='input-container'>
                    <div className='label-wrapper'>
                      <label className='input-label' htmlFor="confirm_password" style={{ color: fontColor }}>Confirm Password</label>
                    </div>
                    <Field
                      className='form-input'
                      type='password'
                      placeholder={'Re-enter your password'}
                      name='confirm_password'
                      style={{ borderColor: fontColor }}
                    />
                    <ErrorMessage className='error' component="div" name='confirm_password' />
                  </div>
                  <div className='input-container'>
                    <div className='label-wrapper'>
                      <label className='input-label' htmlFor="employer_name" style={{ color: fontColor }}>Company Name</label>
                    </div>
                    <Field
                      className='form-input'
                      type='text'
                      placeholder={'Enter your company name'}
                      name='employer_name'
                      style={{ borderColor: fontColor }}
                    />
                    <ErrorMessage className='error' component="div" name='employer_name' />
                  </div>
                  <div className='input-container'>
                    <div className='label-wrapper'>
                      <label className='input-label' htmlFor="employer_code" style={{ color: fontColor }}>Employer Code</label>
                    </div>
                    <Field
                      className='form-input'
                      type='text'
                      placeholder={'Enter your employer code'}
                      name='employer_code'
                      style={{ borderColor: fontColor }}
                    />
                    <ErrorMessage className='error' component="div" name='employer_code' />
                  </div>
                  <div className='input-container'>
                    <div className='label-wrapper'>
                      <label className='input-label' htmlFor="employer_name" style={{ color: fontColor }}>Insurance Carrier (optional)</label>
                    </div>
                    <Field
                      className='form-input'
                      type='text'
                      placeholder={'Enter your insurance carrier'}
                      name='insurance_carrier'
                      style={{ borderColor: fontColor }}
                    />
                    <ErrorMessage className='error' component="div" name='insurance_carrier' />
                  </div>
                <p className='tos'>
                  By creating an account, you agree to Sunset Health's 
                  <a href='https://www.sunset.health/tos/'> Terms of Service </a>
                  and 
                  <a href='https://www.sunset.health/pp'> Privacy Policy </a>
                </p>
                {
                  orgError && (
                    <div className='error org-error'>
                      Sorry, we were unable to locate your organization. Please check your credentials and try again, or contact us for one of our representatives to reach out.
                    </div>
                  )
                }
                {
                  emailError && (
                    <div className='error org-error'>
                      Seems like an account was created before. Please login to continue to payment.
                    </div>
                  )
                }
                {
                  unkError && (
                    <div className='error org-error'>
                      Sorry, we are unable to submit the form. Please try again in a few minutes, or contact us for one of our representatives to reach out.
                    </div>
                  )
                }
                <div className='button_container'>
                  {
                    loading && <Loader /> 
                  }
                  {
                    !loading && (<button
                      className='button button_signup button_text'
                      type='submit'
                      disabled={!(formik.isValid && formik.dirty)}
                      style={{ backgroundColor: (formik.isValid && formik.dirty) ? fontColor : '#cecece', transition: 'all 0.3s ease 0s' }}
                    >
                      SIGN UP NOW
                    </button>)
                  }
                </div>
              </Form>
            )
          }
        }
      </Formik>
    </>
  )
}

export default SignUpForm